/**
 * Grouping and summary helpers for a Constraint list — the Solution Constraints card
 * today, the Account Constraint Profile card when it migrates to the same picker.
 *
 * Pulled out of the component so the ordering is testable without a DOM, and so every
 * surface that lists Constraints by group agrees on it. The API returns rows in table
 * order, which is not an order a reader can predict.
 */

import { CONSTRAINT_GROUP_ORDER } from "@/lib/constraint-colors";
import type { SelectOption } from "@/lib/option-search";

/** The minimum a Constraint row needs to be bucketed. */
export interface GroupedConstraint {
  id: string;
  name: string;
  group: string;
}

export interface ConstraintBucket<T extends GroupedConstraint> {
  group: string;
  items: T[];
}

/** Same settings as option-search.ts: "Phase 2" before "Phase 10", case folded. */
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const CANONICAL_GROUPS: ReadonlySet<string> = new Set(CONSTRAINT_GROUP_ORDER);

/**
 * Buckets by group in canonical order — every canonical group is present even when
 * empty, so a card always renders the same four rows — followed by any other group the
 * data carries, alphabetically. Nothing is dropped: the schema pins groups to the
 * canonical four, but a row the UI silently hides is exactly the bug this guards
 * against. Items within a bucket are sorted by name.
 */
export function bucketByGroup<T extends GroupedConstraint>(
  items: readonly T[],
): ConstraintBucket<T>[] {
  const byGroup = new Map<string, T[]>();
  for (const group of CONSTRAINT_GROUP_ORDER) byGroup.set(group, []);
  for (const item of items) {
    const bucket = byGroup.get(item.group);
    if (bucket) bucket.push(item);
    else byGroup.set(item.group, [item]);
  }
  const extras = [...byGroup.keys()].filter((g) => !CANONICAL_GROUPS.has(g)).sort(COLLATOR.compare);
  return [...CONSTRAINT_GROUP_ORDER, ...extras].map((group) => ({
    group,
    items: [...(byGroup.get(group) ?? [])].sort((a, b) => COLLATOR.compare(a.name, b.name)),
  }));
}

export interface ConstraintSummary {
  total: number;
  /** Copied from the Account at Solution creation (§4.3 Rule 10), as opposed to added here. */
  inherited: number;
}

export function summarizeConstraints(items: readonly { inherited: boolean }[]): ConstraintSummary {
  let inherited = 0;
  for (const item of items) if (item.inherited) inherited += 1;
  return { total: items.length, inherited };
}

/**
 * The one-line summary above the Solution Constraints card. Leads with the count the
 * Fit Score depends on, then with how many came from the Account — the fact a reader
 * wants before removing one.
 */
export function constraintSummaryText(summary: ConstraintSummary, accountName: string): string {
  if (summary.total === 0) {
    return "None yet — the Fit Score stays unavailable until at least one Constraint is added.";
  }
  const required = `${summary.total} required`;
  if (summary.inherited === 0) return `${required} · none inherited from ${accountName}`;
  if (summary.inherited === summary.total) return `${required} · all inherited from ${accountName}`;
  return `${required} · ${summary.inherited} inherited from ${accountName}`;
}

/** A Constraint as the picker lists it: a SelectOption plus the library's description. */
export interface ConstraintPickerOption extends SelectOption {
  value: string;
  label: string;
  group: string;
  description: string | null;
}

/** A library row, as `/api/dashboard/reference` returns it. */
export interface ConstraintLibraryRow {
  id: string;
  name: string;
  groupName: string;
  description: string | null;
}

/**
 * Library rows → picker options, so `matchesQuery` / `groupOptions` from option-search
 * apply unchanged. Ordered canonical-group-first (via `bucketByGroup`) so the grouped
 * list reads Domain → Platform → Offering → ICP rather than alphabetically by group.
 */
export function toPickerOptions(
  library: readonly ConstraintLibraryRow[],
): ConstraintPickerOption[] {
  const rows = library.map((c) => ({
    id: c.id,
    name: c.name,
    group: c.groupName,
    description: c.description,
  }));
  return bucketByGroup(rows).flatMap((bucket) =>
    bucket.items.map((c) => ({
      value: c.id,
      label: c.name,
      group: c.group,
      description: c.description,
    })),
  );
}
